import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { FastForward, Info } from 'lucide-react';
import { Tooltip } from '@coverright/ui/inputs';
import { AgentCard } from './checkout-agent-card';
import { TrustSection } from './checkout-trust-section';

// ─── Shimmer animation ────────────────────────────────────────────────────────

const shimmerAnim = keyframes`
  from { background-position: 0% center; }
  to   { background-position: -200% center; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Step2Props {
  onClose: () => void;
}

export const CheckoutStep2 = ({ onClose }: Step2Props) => (
  <Stack direction={{ xs: 'column', md: 'row' }} gap={4} alignItems={{ md: 'flex-start' }}>

    {/* ── Left col ── */}
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#101828', mb: 1, lineHeight: 1.3 }}>
        Next, connect with your licensed agent
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#4b5563', lineHeight: 1.7, mb: 1.5 }}>
        To move forward, you'll need to hop on a brief phone call with one of our licensed experts
        to make sure you're getting the right coverage for your needs.
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic', lineHeight: 1.6, mb: 2.5 }}>
        This choice is non-binding, and you will have the opportunity to change it at any point.
      </Typography>

      {/* FastTrack banner */}
      <Box sx={{
        bgcolor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 1.5, p: 2,
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'stretch' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 0 },
        overflow: 'hidden',
      }}>
        {/* Orange left accent bar */}
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 4, bgcolor: '#d95a2b', borderRadius: '12px 0 0 12px',
        }} />

        {/* Left: branding + phone */}
        <Box sx={{
          pl: 1, flexShrink: 0,
          pr: { sm: 3 },
          display: 'flex', flexDirection: 'column',
          alignItems: { xs: 'center', sm: 'flex-start' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
            background: 'linear-gradient(110deg, #d95a2b 45%, #ffb9a1 55%, #d95a2b)',
            backgroundSize: '200% auto', backgroundClip: 'text', color: 'transparent',
            animation: `${shimmerAnim} 2s infinite`,
            mb: 0.5,
          }}>
            You've been selected for
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
            <FastForward size={20} color="#d95a2b" />
            <Typography sx={{
              fontSize: 18, fontWeight: 900, letterSpacing: '0.05em',
              background: 'linear-gradient(110deg, #d95a2b 45%, #ffb9a1 55%, #d95a2b)',
              backgroundSize: '200% auto', backgroundClip: 'text', color: 'transparent',
              animation: `${shimmerAnim} 2s infinite`,
            }}>
              FASTTRACK
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#101828', mb: 0.75 }}>
            +1 888 969 7667
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Tooltip title="Due to insurance carrier requirements and legal compliance, we need to confirm your information over the phone. You can also use this time to ask our licensed experts any questions — their advice is totally unbiased.">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#d95a2b' }}>Why a phone call</Typography>
                <Info size={12} color="#d95a2b" />
              </Stack>
            </Tooltip>
          </Box>
        </Box>

        {/* Right: skip the wait */}
        <Box sx={{
          flex: 1,
          borderLeft: { sm: '1px solid #f3f4f6' },
          pl: { sm: 3 },
          display: 'flex', alignItems: 'center',
          textAlign: { xs: 'center', sm: 'left' },
        }}>
          <Typography sx={{ fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
            <Box component="span" sx={{ fontWeight: 700, color: '#101828' }}>Skip the wait.</Box>{' '}
            If you call now, your request can be processed immediately.
          </Typography>
        </Box>
      </Box>

      {/* Trust logos (desktop) */}
      <TrustSection sx={{ display: { xs: 'none', md: 'flex' } }} />
    </Box>

    {/* ── Right col: agent card ── */}
    <AgentCard />

    {/* Trust logos (mobile) */}
    <TrustSection sx={{ display: { xs: 'flex', md: 'none' }, mt: 2 }} />
  </Stack>
);
